/**
 * @fileoverview Pure rules for the B2B event form: audience, duration, and viewport.
 * @module pages/b2bEventForm/b2bEventFormModel
 */

/** Date format used for API and display (ISO date). */
export const DATE_FORMAT = "YYYY-MM-DD";

export const AUDIENCE_SOCIOS = 0;
export const AUDIENCE_NO_SOCIOS = 1;
export const AUDIENCE_BOTH = 2;

const AUDIENCE_SOCIOS_TEXT = "0";
const AUDIENCE_NO_SOCIOS_TEXT = "1";
const AUDIENCE_BOTH_TEXT = "2";

const MINUTES_PER_HOUR = 60;
const DURATION_INPUT_PATTERN = /^([0-9]|1[0-9]|2[0-4]):[0-5][0-9]$/;

export const LAPTOP_MIN_WIDTH = 1024;
export const LAPTOP_MAX_WIDTH = 1440;
export const TABLET_MAX_WIDTH = 1023;
export const TABLET_MIN_WIDTH = 768;
export const MEDIUM_MOBILE_MAX_WIDTH = 767;
export const MEDIUM_MOBILE_MIN_WIDTH = 481;
export const SMALL_MOBILE_MAX_WIDTH = 480;

export const BANNER_MAX_BYTES = 5 * 1024 * 1024;
export const BANNER_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const SEARCH_BAR_WIDTH_LAPTOP = "90%";
const SEARCH_BAR_WIDTH_TABLET = "73%";
const SEARCH_BAR_WIDTH_COMPACT = "100%";
const SEARCH_BAR_WIDTH_DESKTOP = "90%";

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export const parseDurationMinutes = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  if (!DURATION_INPUT_PATTERN.test(text)) {
    return null;
  }
  const [hours, minutes] = text.split(":").map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

/**
 * A duration is valid only when it parses to more than zero minutes.
 * @param {unknown} value
 * @returns {boolean}
 */
export const isAppointmentDurationValid = (value) => {
  const minutes = parseDurationMinutes(value);
  return minutes != null && minutes > 0;
};

/**
 * @param {unknown} audiencia
 * @returns {boolean}
 */
export const isSociosAudience = (audiencia) =>
  audiencia === AUDIENCE_SOCIOS ||
  audiencia === AUDIENCE_SOCIOS_TEXT ||
  audiencia === AUDIENCE_BOTH ||
  audiencia === AUDIENCE_BOTH_TEXT;

/**
 * @param {unknown} audiencia
 * @returns {boolean}
 */
export const isNoSociosAudience = (audiencia) =>
  audiencia === AUDIENCE_NO_SOCIOS ||
  audiencia === AUDIENCE_NO_SOCIOS_TEXT ||
  audiencia === AUDIENCE_BOTH ||
  audiencia === AUDIENCE_BOTH_TEXT;

/**
 * @param {unknown} audiencia
 * @returns {boolean}
 */
export const isAudienciaChosen = (audiencia) =>
  isSociosAudience(audiencia) || isNoSociosAudience(audiencia);

/**
 * Members, non-members, both, or null when neither box stays checked.
 * @param {unknown} current
 * @param {"socios"|"noSocios"} which
 * @returns {number|null}
 */
export const nextB2BAudience = (current, which) => {
  const sociosWas = isSociosAudience(current);
  const noSociosWas = isNoSociosAudience(current);
  const socios = which === "socios" ? !sociosWas : sociosWas;
  const noSocios = which === "noSocios" ? !noSociosWas : noSociosWas;

  if (socios && noSocios) {
    return AUDIENCE_BOTH;
  } else if (socios) {
    return AUDIENCE_SOCIOS;
  } else if (noSocios) {
    return AUDIENCE_NO_SOCIOS;
  } else {
    return null;
  }
};

/**
 * @param {unknown} costo
 * @returns {boolean}
 */
export const isCostFilled = (costo) => {
  if (costo === null || costo === undefined) {
    return false;
  }
  const text = String(costo).trim();
  if (text === "") {
    return false;
  }
  const amount = Number(text);
  return Number.isFinite(amount) && amount >= 0;
};

/**
 * Snapshot of the width buckets taken once per render.
 * @param {number} width
 * @returns {Object}
 */
export const readFormViewportFlags = (width) => ({
  isLaptop: width >= LAPTOP_MIN_WIDTH && width <= LAPTOP_MAX_WIDTH,
  isTablet: width <= TABLET_MAX_WIDTH,
  isMediumMobile: width <= MEDIUM_MOBILE_MAX_WIDTH,
  isSmallMobile: width <= SMALL_MOBILE_MAX_WIDTH,
});

/**
 * Smaller buckets win when widths overlap. Flags come from the current render.
 * @param {Object} flags
 * @returns {string}
 */
export const formDeviceName = (flags) => {
  if (flags.isSmallMobile) {
    return "smallMobile";
  }
  if (flags.isMediumMobile) {
    return "mediumMobile";
  }
  if (flags.isLaptop) {
    return "laptop";
  }
  if (flags.isTablet) {
    return "tablet";
  }
  return "desktop";
};

/**
 * Live width check. Tablet is 768–1023, unlike the render-time tablet flag.
 * @param {number} [width]
 * @returns {boolean}
 */
export const isTabletViewport = (width = window.innerWidth) =>
  width >= TABLET_MIN_WIDTH && width <= TABLET_MAX_WIDTH;

/**
 * @param {number} [width]
 * @returns {boolean}
 */
export const isMediumMobileViewport = (width = window.innerWidth) =>
  width >= MEDIUM_MOBILE_MIN_WIDTH && width <= MEDIUM_MOBILE_MAX_WIDTH;

/**
 * @param {number} [width]
 * @returns {boolean}
 */
export const isSmallMobileViewport = (width = window.innerWidth) =>
  width <= SMALL_MOBILE_MAX_WIDTH;

/**
 * Participant add/edit uses the full page on tablet and mobile, a modal on desktop.
 * @param {number} [width]
 * @returns {boolean}
 */
export const participantFlowUsesFullPageView = (width = window.innerWidth) =>
  isTabletViewport(width) ||
  isMediumMobileViewport(width) ||
  isSmallMobileViewport(width);

/**
 * Width buckets aligned with the B2B event details search bar.
 * @param {string} device
 * @returns {string}
 */
export const participatingCompaniesSearchBarWidthFor = (device) => {
  if (device === "laptop") {
    return SEARCH_BAR_WIDTH_LAPTOP;
  }
  if (device === "tablet") {
    return SEARCH_BAR_WIDTH_TABLET;
  }
  if (device === "mediumMobile" || device === "smallMobile") {
    return SEARCH_BAR_WIDTH_COMPACT;
  }
  return SEARCH_BAR_WIDTH_DESKTOP;
};

/**
 * Compact screens use the device width. Wider screens keep the bar at 100%.
 * @param {Object} flags
 * @returns {string}
 */
export const participatingSearchBarWidth = (flags) => {
  if (flags.isMediumMobile || flags.isSmallMobile) {
    return participatingCompaniesSearchBarWidthFor(formDeviceName(flags));
  }
  return SEARCH_BAR_WIDTH_COMPACT;
};

/**
 * @param {Object} input
 * @returns {boolean}
 */
export const isB2BEventSaveDisabled = ({
  hasChanges,
  saving,
  loading,
  formData,
  nameValidation,
}) =>
  !hasChanges ||
  saving ||
  loading ||
  !formData.nombre ||
  !isAudienciaChosen(formData.audiencia) ||
  !formData.fechaInicioEvento ||
  !formData.fechaFinEvento ||
  !formData.descripcion ||
  !formData.fechaDisponibleSocios ||
  !formData.fechaDisponibleNoSocios ||
  !formData.fechaVigenciaCompradores ||
  !formData.fechaLimiteRegistro ||
  !isCostFilled(formData.costoSocios) ||
  !isCostFilled(formData.costoNoSocios) ||
  !formData.horaInicio ||
  !formData.horaFin ||
  !isAppointmentDurationValid(formData.duracionCitasSocios) ||
  !isAppointmentDurationValid(formData.duracionCitasNoSocios) ||
  !formData.imagenBanner ||
  !nameValidation.isValid;
